import ComparisonModulesRenderer from '@/components/_rebrand/craft/ComparisonModulesRenderer';
import AllFeatures from '@/components/_rebrand/craft/product/AllFeatures';
import CompetitiveTable from '@/components/_rebrand/craft/product/CompetitiveTable';
import FAQ from '@/components/_rebrand/craft/product/FAQ';
import FeatureGrid from '@/components/_rebrand/craft/product/FeatureGrid';
import FeatureHighlight from '@/components/_rebrand/craft/product/FeatureHighlight';
import Features from '@/components/_rebrand/craft/product/Features';
import Hero from '@/components/_rebrand/craft/product/Hero';
import ThreeBenefits from '@/components/_rebrand/craft/product/ThreeBenefits';
import { PageBumper, PageBumperProps } from '@/components/_rebrand/PageBumper';
import { ProductPagePathsQuery, ProductPageQuery, ProductPageQueryVariables } from '@/graphql';
import ProductPageQueryDocument from '@/graphql/queries/ProductPage.gql';
import ProductPagePathsQueryDocument from '@/graphql/queries/ProductPagePaths.gql';
import ModularPageLayout from '@/layouts/ModularPageLayout';
import { getPageProps } from '@/lib/craft-page';
import CraftClient from '@/lib/graphql';
import { getStaticPagePaths, getStaticPageProps, notFoundResponse } from '@/lib/next';
import { ModularPageProps, parseModuleBuilderProps } from '@/modules/ModuleBuilder';
import MasterTemplate from '@/templates/MasterTemplate';
import { Fragment } from 'react';

const BUMPER_PROPS = {
  title: 'Ready to tame the insanity? Come to Front.',
  links: [
    {
      type: 'primary',
      title: 'Request a demo',
      href: '',
      action: 'demo',
    },
    {
      type: 'secondary',
      title: 'Try for free',
      href: '',
      action: 'trial',
    },
  ],
} as PageBumperProps;

const ProductPageHandler: PageTemplate<ProductPageQuery & ModularPageProps> = (props) => {

  const modularPageProps = props.modularPageProps;
  const entry = (modularPageProps && modularPageProps.entry) ? modularPageProps.entry : props.entry;
  const capabilitiesLinks = props.capabilitiesLinks;

  if (!entry) {
    return null;
  }

  if ('__typename' in entry && entry.__typename === 'productPages_rebrandModular_Entry') {
    return <ComparisonModulesRenderer modules={entry.comparisonModules} />;
  }

  if (modularPageProps) {
    return <ModularPageLayout
      props={modularPageProps}
      config={{
        capabilitiesLinkGrid: {
          links: capabilitiesLinks,
        },
      }}
    />
  }

  return (
    <Fragment>
      <Hero entry={entry} />
      <ThreeBenefits entry={entry} />
      <CompetitiveTable entry={entry} />
      <Features entry={entry} />
      <FeatureHighlight entry={entry} />
      <FeatureGrid entry={entry} />
      <AllFeatures entry={entry} />
      <PageBumper {...BUMPER_PROPS} />
      <FAQ entry={entry} />
    </Fragment>
  );

};

ProductPageHandler.template = (props: any) => {
  const invert: boolean = !!isRebrandedProductPageQuery(props);

  return <MasterTemplate invert={invert} className='mt-0' {...props} />;
}

export default ProductPageHandler;

const isRebrandedProductPageQuery = (obj: any): obj is ProductPageQuery => {
  return (
    obj &&
    typeof obj === 'object' &&
    'entry' in obj &&
    obj.entry?.__typename === 'productPages_capabilityNewBrand_Entry'
  );
}

export const getStaticProps = getStaticPageProps<{}, { slug: string[] }>(
  (params) => `product/${params?.slug}`,
  async ({ client, uri }) => {

    const pageProps = await getPageProps<ProductPageQuery, ProductPageQueryVariables>({
      pathPrefix: '/product',
      queryDocument: ProductPageQueryDocument,
      client,
      variables: {
        includeLinks: true,
        uri
      },
    });


    if (pageProps?.entry?.__typename === 'productPages_rebrandModular_Entry') {
      return {
        props: {
          entry: pageProps.entry,
        },
      };
    }

    if (pageProps && !isRebrandedProductPageQuery(pageProps)) {
      return {
        props: parseModuleBuilderProps(pageProps),
      };
    }

    if (pageProps?.entry) {
      return {
        props: {
          entry: pageProps.entry,
        },
      };
    }

    /* If all else fails, just 404 */
    return notFoundResponse;
  },
);

export const getStaticPaths = getStaticPagePaths('product', async (context) => {
  const client = new CraftClient();

  const data = await client.request<ProductPagePathsQuery>(ProductPagePathsQueryDocument);

  const paths =
    data.entries?.map((path) => ({
      params: {
        slug: path?.slug!,
      },
    })) || [];

  return {
    paths,
    fallback: 'blocking',
  };
});
